import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Grid,
  Typography,
  alpha,
} from "@mui/material";
import { useCertifications } from "../../Query/useCertifications";
import { useTranslation } from "react-i18next";
import colors from "../../Constants/colors";

interface CertificationsSelectorProps {
  selectedCertifications: number[];
  onChange: (selected: number[]) => void;
}

const CertificationsSelector: React.FC<CertificationsSelectorProps> = ({
  selectedCertifications,
  onChange,
}) => {
  const { data: certifications = [] } = useCertifications();
  const { t } = useTranslation("workspaces");

  const handleToggle = (id: number) => {
    const updated = selectedCertifications.includes(id)
      ? selectedCertifications.filter((x) => x !== id)
      : [...selectedCertifications, id];

    onChange(updated);
  };

  return (
    <Box>
      <Typography
        sx={{
          color: colors.primary_dark,
          fontWeight: 600,
          fontSize: "0.85rem",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          borderBottom: `1px solid ${colors.gray200}`,
          pb: 0.5,
          mb: 2,
        }}
      >
        {t("certifications")}
      </Typography>

      <Grid container spacing={1.5}>
        {certifications.map((cert) => {
          const isSelected = selectedCertifications.includes(cert.id);

          return (
            <Grid item xs={12} sm={6} md={4} key={cert.id}>
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${
                    isSelected ? colors.accent : colors.gray200
                  }`,
                  borderLeft: `4px solid ${
                    isSelected ? colors.accent : colors.primary_dark
                  }`,
                  backgroundColor: isSelected
                    ? alpha(colors.accent, 0.08)
                    : colors.white,
                  borderRadius: 1.5,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: colors.accent,
                    borderLeftColor: colors.accent,
                  },
                }}
              >
                <CardActionArea onClick={() => handleToggle(cert.id)}>
                  <CardContent sx={{ py: 1.25, px: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        sx={{
                          color: colors.gray400,
                          "&.Mui-checked": {
                            color: colors.accent,
                          },
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: isSelected ? 600 : 500,
                          color: colors.primary_dark,
                        }}
                      >
                        {cert.name}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default CertificationsSelector;
